/**
 * 学员端 - 我的纠错页面
 *
 * 功能:
 * 1. 展示学员提交的所有纠错记录列表
 * 2. 按状态筛选: 全部 / 待审核 / 已通过 / 已拒绝
 * 3. 每条记录显示: 纠错类型、错误描述、提交时间、审核状态、奖励积分
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase/client';
import { Clock, CheckCircle, XCircle, AlertCircle, ChevronLeft, Filter } from 'lucide-react';

interface Correction {
  id: string;
  target_type: string;
  target_id: string;
  target_detail: any;
  error_description: string;
  suggested_correction: string | null;
  severity: string | null;
  status: string;
  reviewer_id: string | null;
  review_comment: string | null;
  reviewed_at: string | null;
  reward_points: number | null;
  rewarded_at: string | null;
  created_at: string;
  updated_at: string;
}

const TARGET_TYPE_LABELS: Record<string, string> = {
  question: '题目纠错',
  paper: '试卷纠错',
  answer: '答案纠错',
  option: '选项纠错',
  explanation: '解析纠错',
  other: '其他问题',
};

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
  pending: { label: '待审核', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  reviewing: { label: '审核中', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  approved: { label: '已通过', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: '已拒绝', color: 'bg-red-100 text-red-700', icon: XCircle },
  rewarded: { label: '已奖励', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  processed: { label: '已处理', color: 'bg-gray-100 text-gray-700', icon: CheckCircle },
};

const SEVERITY_LABELS: Record<string, string> = {
  minor: '轻微',
  moderate: '中等',
  major: '严重',
};

const MyCorrections: React.FC = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [corrections, setCorrections] = useState<Correction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchCorrections();
    }
  }, [userId, filterStatus]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setUserId(user.id);
    } else {
      setLoading(false);
    }
  };

  const fetchCorrections = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('error_corrections')
        .select('*')
        .eq('submitter_id', userId!)
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Failed to fetch corrections:', error);
      } else {
        setCorrections(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch corrections:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusCounts = {
    all: corrections.length,
    pending: corrections.filter(c => c.status === 'pending').length,
    approved: corrections.filter(c => c.status === 'approved' || c.status === 'rewarded').length,
    rejected: corrections.filter(c => c.status === 'rejected').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">加载中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">我的纠错</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* 统计概览 */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-yellow-50 rounded-xl p-4 text-center border border-yellow-100">
            <div className="text-2xl font-bold text-yellow-600">{statusCounts.pending}</div>
            <div className="text-xs text-yellow-500 mt-1">待审核</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4 text-center border border-green-100">
            <div className="text-2xl font-bold text-green-600">{statusCounts.approved}</div>
            <div className="text-xs text-green-500 mt-1">已通过</div>
          </div>
          <div className="bg-red-50 rounded-xl p-4 text-center border border-red-100">
            <div className="text-2xl font-bold text-red-600">{statusCounts.rejected}</div>
            <div className="text-xs text-red-500 mt-1">已拒绝</div>
          </div>
        </div>

        {/* 状态筛选 */}
        <div className="bg-white rounded-xl p-4 mb-4 shadow-sm">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <Filter className="w-4 h-4 text-gray-400" />
            {[
              { key: 'all', label: '全部' },
              { key: 'pending', label: '待审核' },
              { key: 'approved', label: '已通过' },
              { key: 'rejected', label: '已拒绝' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilterStatus(key)}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  filterStatus === key
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {label}
                {key !== 'all' && statusCounts[key as keyof typeof statusCounts] > 0 && (
                  <span className="ml-1 text-xs opacity-75">
                    ({statusCounts[key as keyof typeof statusCounts]})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* 纠错记录列表 */}
        {corrections.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">✏️</div>
            <div className="text-gray-500 mb-2">暂无纠错记录</div>
            <div className="text-sm text-gray-400">答题时发现错误可以提交纠错，审核通过后可获得积分奖励</div>
          </div>
        ) : (
          <div className="space-y-3">
            {corrections.map((correction) => {
              const statusInfo = STATUS_CONFIG[correction.status] || { label: correction.status, color: 'bg-gray-100 text-gray-700', icon: Clock };
              return (
                <div key={correction.id} className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* 类型 + 状态 */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                          {TARGET_TYPE_LABELS[correction.target_type] || correction.target_type}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                        {correction.severity && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            {SEVERITY_LABELS[correction.severity] || correction.severity}
                          </span>
                        )}
                      </div>

                      {/* 错误描述 */}
                      <div className="text-sm text-gray-800 mb-1 line-clamp-2">
                        {correction.error_description}
                      </div>

                      {/* 建议修正 */}
                      {correction.suggested_correction && (
                        <div className="text-xs text-gray-500 mb-1">
                          建议: {correction.suggested_correction}
                        </div>
                      )}

                      {/* 审核意见 */}
                      {correction.review_comment && (
                        <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 mt-2">
                          审核意见: {correction.review_comment}
                        </div>
                      )}

                      {/* 时间 */}
                      <div className="text-xs text-gray-400 mt-2">
                        提交于 {formatDate(correction.created_at)}
                        {correction.reviewed_at && ` · 审核于 ${formatDate(correction.reviewed_at)}`}
                      </div>
                    </div>

                    {/* 奖励积分 */}
                    {correction.reward_points && correction.reward_points > 0 && (
                      <div className="text-right flex-shrink-0">
                        <div className="text-lg font-bold text-orange-600">+{correction.reward_points}</div>
                        <div className="text-xs text-gray-400">积分奖励</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCorrections;